import { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Crown, Check, LogOut } from "lucide-react";

export function PaywallPage() {
  const { user, logout, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/billing/status`).then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const subscribe = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/billing/subscribe`);
      const sub = r.data;
      const isNative = window.Capacitor?.isNativePlatform?.();
      if (isNative) {
        // Use native Razorpay plugin (capacitor-razorpay)
        try {
          const { Checkout } = await import("capacitor-razorpay");
          const result = await Checkout.open({
            key: status?.razorpay_key_id,
            subscription_id: sub.subscription_id,
            name: "Rachi Algo Console",
            description: "Monthly Pro Subscription",
            prefill: { email: user?.email, name: user?.name, contact: user?.phone || "" },
            theme: { color: "#f59e0b" },
          });
          if (result?.response?.razorpay_payment_id) {
            toast.success("Payment successful! Activating subscription…");
            setTimeout(refresh, 2000);
          }
        } catch (err) {
          // Fallback: open Razorpay hosted page in external browser
          if (sub.short_url && window.Capacitor?.Plugins?.Browser) {
            await window.Capacitor.Plugins.Browser.open({ url: sub.short_url });
          } else if (sub.short_url) {
            window.open(sub.short_url, "_blank");
          }
          toast.message("Razorpay opened in browser — complete payment there");
        }
      } else if (window.Razorpay) {
        // Web Razorpay Checkout
        const options = {
          key: status?.razorpay_key_id,
          subscription_id: sub.subscription_id,
          name: "Rachi Algo Console",
          description: "Monthly Pro Subscription",
          prefill: { email: user?.email, name: user?.name },
          theme: { color: "#f59e0b" },
          handler: async () => {
            toast.success("Payment successful! Activating subscription…");
            setTimeout(refresh, 2000);
          },
        };
        new window.Razorpay(options).open();
      } else if (sub.short_url) {
        window.open(sub.short_url, "_blank");
        toast.message("Opening Razorpay checkout…");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const trialDaysLeft = (() => {
    if (!status?.trial_ends_at) return 0;
    const ms = new Date(status.trial_ends_at).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86400000));
  })();

  return (
    <div className="min-h-screen bg-black text-zinc-100 grid place-items-center p-4">
      <Card className="bg-zinc-950/80 border-zinc-800 p-6 sm:p-10 w-full max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded bg-gradient-to-br from-amber-400 to-orange-600 grid place-items-center font-black text-black text-lg">R</div>
            <div className="text-lg font-bold">Subscription Required</div>
          </div>
          <Button onClick={logout} variant="ghost" size="sm" className="text-zinc-400" data-testid="logout-btn"><LogOut className="h-4 w-4 mr-1" /> Logout</Button>
        </div>

        {status?.in_trial && (
          <div className="bg-emerald-950/40 border border-emerald-900 rounded-lg px-4 py-3 mb-5 text-sm font-mono text-emerald-300">
            ✨ You're on free trial · <strong>{trialDaysLeft} days left</strong>
          </div>
        )}
        {!status?.in_trial && (
          <div className="bg-rose-950/40 border border-rose-900 rounded-lg px-4 py-3 mb-5 text-sm font-mono text-rose-300">
            Trial expired or subscription inactive · Subscribe to continue
          </div>
        )}

        <div className="bg-zinc-900 rounded-lg p-6 mb-5 border border-amber-500/30">
          <div className="flex items-baseline justify-between mb-3">
            <div className="flex items-center gap-2"><Crown className="h-5 w-5 text-amber-400" /><span className="font-bold text-lg">Pro Plan</span></div>
            <div><span className="text-3xl font-black text-amber-400">₹{status?.price_inr ?? 3000}</span><span className="text-zinc-500 ml-1">/month</span></div>
          </div>
          <ul className="space-y-2 text-sm text-zinc-300">
            {[
              "Full Dhan Super Order integration",
              "NIFTY · BANKNIFTY · SENSEX · MCX simultaneously",
              "Auto strike (ATM/OTM/ITM) + auto expiry",
              "T1/T2/T3 partial targets · Trailing SL · Re-entry",
              "Time scheduler (auto-deploy/exit)",
              "Telegram alerts · TOTP auto-token",
              "AES-encrypted credentials at rest",
            ].map((f) => (
              <li key={f} className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /><span>{f}</span></li>
            ))}
          </ul>
        </div>

        <Button onClick={subscribe} disabled={busy || !status?.razorpay_configured} data-testid="subscribe-btn" className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold h-12 text-base">
          {busy ? "Opening checkout…" : !status?.razorpay_configured ? "Payment gateway pending — contact support" : `Subscribe · ₹${status?.price_inr ?? 3000}/month`}
        </Button>
        <div className="mt-3 text-[11px] text-zinc-500 font-mono text-center">
          Powered by Razorpay · Cancel anytime · Secure
        </div>
      </Card>
    </div>
  );
}
